import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isProduction } from "../firebase/config";
import { analyticsService } from "../services/analyticsService";
import { safeGetItem, safeSessionGetItem, safeSessionSetItem, safeSetItem } from "../utils/localStorageUtils";

const trackCampaignClickEndpoint = isProduction
  ? "https://us-central1-peak-x-937b6.cloudfunctions.net/trackCampaignClick"
  : "https://us-central1-peak-x-staging.cloudfunctions.net/trackCampaignClick";

async function trackCampaignClick(campaignCode: string) {
  try {
    await fetch(trackCampaignClickEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ campaignCode }),
    });
  } catch (error) {
    console.warn("Could not track campaign click:", error);
  }
}

/**
 * Campaign links are `/?code=CODE` (the same shape Peak Admin copies).
 * Coach invite links are `/onboarding?code=CODE` and are left for the wizard.
 */
const CampaignTracker: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname.startsWith("/onboarding")) return;
    const params = new URLSearchParams(location.search);
    const code = params.get("code")?.trim();
    if (!code) return;

    const sessionKey = `campaign_tracked_${code}`;
    if (!safeSessionGetItem(sessionKey)) {
      safeSessionSetItem(sessionKey, Date.now().toString());
      safeSetItem("campaignCode", code);
      trackCampaignClick(code);
      analyticsService.trackCampaign(code);
    } else if (!safeGetItem("campaignCode")) {
      safeSetItem("campaignCode", code);
    }

    params.delete("code");
    const next = params.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : "" },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  return null;
};

export default CampaignTracker;
